// The catalog owns price-bearing choices. Personal wording travels with the cart line.
const clean = value => String(value ?? '').trim();
const normalized = value => clean(value).replace(/[–—]/g, '-').toLowerCase();
const privateTestVariants = {
  15442318459199: [67788551586111, 67788551618879, 67788551651647, 67788551684415],
  15450745635135: [67808676086079, 67808676118847, 67808676151615, 67808676184383],
  15450750222655: [67808681754943, 67808681787711, 67808681820479, 67808681853247]
};


const firstDriveTestScents = ["Sandalwood & Patchouli","Cedar & Leather","Eucalyptus","Frankincense","Bourbon","Teakwood","Patchouli & Cedarwood","Lavender","Sweet Orange","Gunsmoke","Vintage Leather & Tabac","Cedarwood & Sage"];
function matchesFirstDriveTestCatalog(catalog) {
  if (catalog?.id !== 15442318459199 || catalog.testOrdering !== true || catalog.variants?.length !== 156 || JSON.stringify(catalog.options) !== JSON.stringify(['Format and scent','Engraving','Material'])) return false;
  const formats = ['Unscented ornament', ...firstDriveTestScents];
  const engravings = ['Front only','Front + back','Front only + backing','Front + back + backing'];
  const woods = ['Birch Ply','Walnut','Cedar'];
  const ids = new Set(), combinations = new Set();
  return catalog.variants.every(v => {
    const [format, engraving, wood] = v.options || [];
    if (v.options?.length !== 3 || !formats.includes(format) || !engravings.includes(engraving) || !woods.includes(wood) || !Number.isSafeInteger(v.id) || v.id <= 0 || ids.has(v.id)) return false;
    const key = JSON.stringify(v.options);
    if (combinations.has(key)) return false;
    ids.add(v.id); combinations.add(key);
    return v.price === 1500 + (format === 'Unscented ornament' ? 0 : 500) + (wood === 'Birch Ply' ? 0 : 500) + (engraving.startsWith('Front + back') ? 300 : 0) + (engraving.endsWith(' + backing') ? 500 : 0);
  });
}

// Count customer-entered lines; visual wrapping and final fit are reviewed in the proof.
export function backMessageFitsLines(value, maxLines = 3) {
  return clean(value).split(/\r\n|[\r\n\u2028\u2029]/).length <= maxLines;
}

export function buildOrderProperties(product, draft, scents) {
  const custom = draft.mode === 'custom';
  const scented = product.scent && draft.format === 'Scented air freshener';
  const back = product.back && draft.back;
  const properties = {
    'Personalization': custom ? 'My own text' : 'Design as shown',
    'Front engraving': custom ? clean(draft.text) : clean(product.shown),
    'Format': product.scent ? draft.format : 'Key fob',
    'Engraving': back ? 'Front + back' : 'Front only',
    '_vador_workflow': custom || back ? 'standard-personalized-proof-required' : 'standard-as-shown'
  };
  if (product.material) properties.Material = clean(draft.material);
  if (draft.backing) {
    properties._vador_backing = 'yes';
    properties._vador_backing_finish = clean(draft.backingFinish);
    properties._vador_backing_ink = clean(draft.backingInk);
  }
  if (custom && product.number) properties.Number = clean(draft.number);
  if (custom && product.message) properties['Message or year'] = clean(draft.message);
  if (scented) properties.Scent = scents.find(scent => scent.value === draft.scent)?.label || '';
  if (back) { properties['Back message'] = clean(draft.backMessage); if (product.material) properties._vador_back_type = clean(draft.backType || 'text'); }
  properties['Special instructions'] = clean(draft.notes);
  return Object.fromEntries(Object.entries(properties).filter(([, value]) => value !== ''));
}

export function resolveOrderVariant(catalog, properties) {
  if (!catalog) return null;
  // Unknown option structures fail closed instead of choosing a default variant.
  const choices = { 'Format': properties.Format, 'Format and scent': properties.Scent || properties.Format, 'Engraving': properties.Engraving + (properties._vador_backing === 'yes' ? ' + backing' : ''), 'Material': properties.Material };
  const materialCatalog = catalog.options.includes('Material');
  if (Boolean(properties.Material) !== materialCatalog) return null;
  if (catalog.options.length !== (materialCatalog ? 3 : 2) || new Set(catalog.options).size !== catalog.options.length || catalog.options.some(option => !choices[option])) return null;
  return catalog.variants.find(variant => catalog.options.every((option, index) =>
    normalized(variant.options[index]) === normalized(choices[option])
  )) || null;
}

export function canPurchase({ enabled, catalog, variant, properties, quantity, pricing }) {
  if (pricing?.requireMaterial && !pricing.materials?.some(item => item.name === properties.Material)) return false;
  if (pricing?.profile === 'standard-ornaments' && properties.Engraving === 'Front + back' && !backMessageFitsLines(properties['Back message'])) return false;
  if (pricing?.pricingModel === 'base-plus-upgrades') {
    const wood = pricing.materials?.find(item => item.name === properties.Material);
    if (!Number.isFinite(wood?.upgradeAmount) || wood.upgradeAmount < 0 || !(pricing.amounts?.base > 0)) return false;
    if (properties.Engraving === 'Front + back' && properties._vador_back_type && properties._vador_back_type !== 'text') return false;
    const components = [pricing.amounts.base, wood.upgradeAmount];
    if (properties.Format === 'Scented air freshener') components.push(pricing.amounts.scent);
    if (properties._vador_backing === 'yes') {
      if (!pricing.backingFinishes?.some(f => f.name === properties._vador_backing_finish && f.engraving === properties._vador_backing_ink)) return false;
      components.push(pricing.amounts.backing);
    }
    if (properties.Engraving === 'Front + back') components.push(pricing.amounts.back);
    if (components.some(amount => !Number.isFinite(amount) || amount < 0) || Math.round(components.reduce((sum, amount) => sum + amount, 0) * 100) !== variant?.price) return false;
  }
  const testIds = privateTestVariants[catalog?.id] || [];
  const isolatedTest = matchesFirstDriveTestCatalog(catalog) || catalog?.testOrdering === true && testIds.length === 4
    && catalog.variants?.length === 4 && new Set(catalog.variants.map(item => item.id)).size === 4
    && catalog.variants.every(item => testIds.includes(item.id))
    && testIds.includes(variant?.id) && (catalog.id === 15442318459199 || properties.Material === 'Birch Ply');
  // Native ready catalogs can use the shared display components without being price-pending.
  // QA IDs never use this path, and the section purchasing toggle is still required below.
  const standardCatalogReady = Number.isSafeInteger(catalog?.id) && catalog.id > 0
    && catalog.pending === false && catalog.testOrdering === false && !privateTestVariants[catalog.id];
  if (pricing?.pending && !isolatedTest && !standardCatalogReady) return false;
  if (!enabled || !catalog || catalog.pending || !variant?.available || !(variant.price > 0)) return false;
  if (!Number.isSafeInteger(quantity) || quantity < 1) return false;
  if (properties.Personalization === 'My own text' && ![properties['Front engraving'], properties.Number, properties['Message or year']].some(clean)) return false;
  if (properties.Engraving === 'Front + back' && !clean(properties['Back message'])) return false;
  if (catalog.options.includes('Format') && properties.Format === 'Scented air freshener' && !firstDriveTestScents.includes(properties.Scent)) return false;
  if (properties.Format === 'Scented air freshener' && (!properties.Scent || (/placeholder/i.test(properties.Scent) && !isolatedTest))) return false;
  const rule = variant.quantity_rule || { min: 1, increment: 1, max: null };
  return quantity >= rule.min && quantity % rule.increment === 0 && (rule.max == null || quantity <= rule.max);
}

// Blank, malformed, negative or duplicate wood entries stay unpriced; zero must be explicit.
export function applyWoodUpcharges(materials, raw) {
  const entries = new Map();
  for (const line of String(raw ?? '').split(/\r?\n/)) {
    const parts = line.split('|'), name = clean(parts[0]), value = clean(parts[1]);
    if (!name) continue;
    const amount = parts.length === 2 && /^(?:0|[1-9]\d*)(?:\.\d{1,2})?$/.test(value) ? Number(value) : null;
    entries.set(name, entries.has(name) || !Number.isFinite(amount) || !Number.isSafeInteger(Math.round(amount * 100)) ? null : amount);
  }
  return materials.map(material => ({...material,upgradeAmount:entries.get(material.name) ?? null}));
}

// Preview components only. Catalog prices remain the source of real checkout totals.
export function buildOrnamentPricingPreview(profile, draft) {
  if (profile?.profile !== 'standard-ornaments' || !profile.pending) return [];
  const material = profile.materials?.find(item => item.name === draft.material);
  if (profile.pricingModel === 'base-plus-upgrades') {
    const parts = [{key:'base',title:profile.baseTitle,label:profile.baseLabel,amount:profile.amounts.base}];
    if (draft.material) parts.push({key:'wood',title:(material?.name || draft.material) + (material?.upgradeAmount === 0 ? '' : ' · ' + profile.woodTitle),label:material?.upgradeLabel || profile.woodPending,amount:material?.upgradeAmount ?? null});
    if (draft.format === 'Scented air freshener') parts.push({key:'scent',title:profile.scentTitle,label:profile.scentLabel,amount:profile.amounts.scent});
    if (draft.back) {
      const unavailable = draft.backType && draft.backType !== 'text';
      parts.push({key:'back',title:unavailable ? profile.customBackTitle : profile.backTitle,label:unavailable ? profile.unavailable : profile.backLabel,amount:unavailable ? null : profile.amounts.back});
    }
    if (draft.backing) parts.push({key:'backing',title:profile.backingTitle || 'Leatherette backing',label:'+' + new Intl.NumberFormat(undefined,{style:'currency',currency:'USD'}).format(profile.amounts.backing),amount:profile.amounts.backing});
    return parts;
  }
  const parts = [{key:'base', title:material ? material.name + ' · ' + profile.materialBaseSuffix : profile.baseTitle, label:material?.priceLabel || profile.baseLabel, amount:null}];
  if (draft.format === 'Scented air freshener') parts.push({key:'scent', title:profile.scentTitle, label:profile.scentLabel, amount:null});
  if (draft.back) parts.push({key:'back', title:profile.backTitle, label:profile.backLabel, amount:null});
  return parts;
}

// Display-only item totals fail closed when any selected price or quantity is unknown.
export function ornamentPreviewTotal(profile, draft) {
  if (profile?.pricingModel !== 'base-plus-upgrades' || !draft.material) return null;
  const parts = buildOrnamentPricingPreview(profile, draft);
  const quantity = Number(draft.quantity);
  if (!parts.length || !parts.some(part => part.key === 'wood') || !Number.isSafeInteger(quantity) || quantity < 1) return null;
  if (parts.some(part => !Number.isFinite(part.amount) || part.amount < 0)) return null;
  const unitPrice = parts.reduce((sum, part) => sum + Math.round(part.amount * 100), 0);
  const total = unitPrice * quantity;
  return unitPrice > 0 && Number.isSafeInteger(unitPrice) && Number.isSafeInteger(total) ? {unitPrice, total} : null;
}

// A local rehearsal uses real catalog IDs/prices but never enables Shopify purchasing.
export function buildPreviewCartLine({product, catalog, draft, scents, pricing}) {
  if (pricing?.pricingModel !== 'base-plus-upgrades' || !product.material) return null;
  const properties = buildOrderProperties(product, draft, scents);
  const variant = resolveOrderVariant(catalog, properties);
  const wood = pricing.materials?.find(item => item.name === properties.Material);
  const quantity = Number(draft.quantity);
  if (!variant || !Number.isFinite(wood?.upgradeAmount) || wood.upgradeAmount < 0) return null;
  if (draft.backType && draft.backType !== 'text') return null;
  if (draft.mode === 'custom' && !clean(draft.text)) return null;
  if (draft.back && (!clean(draft.backMessage) || !backMessageFitsLines(draft.backMessage))) return null;
  if (draft.format === 'Scented air freshener' && !properties.Scent) return null;
  const amounts = [pricing.amounts.base, wood.upgradeAmount];
  if (draft.format === 'Scented air freshener') amounts.push(pricing.amounts.scent);
  if (draft.back) amounts.push(pricing.amounts.back);
  if (draft.backing) {
    if (!pricing.backingFinishes?.some(f => f.name === properties._vador_backing_finish && f.engraving === properties._vador_backing_ink)) return null;
    amounts.push(pricing.amounts.backing);
  }
  if (amounts.some(amount => !Number.isFinite(amount) || amount < 0)) return null;
  const expected = Math.round(amounts.reduce((sum, amount) => sum + amount, 0) * 100);
  if (!(expected > 0) || expected !== variant.price || !Number.isSafeInteger(expected * quantity)) return null;
  const line = {id: variant.id, title: catalog.title, quantity, properties, unitPrice: variant.price,
    quantityRule: variant.quantity_rule || {min: 1, increment: 1, max: null}};
  return validPreviewQuantity(line, quantity) ? line : null;
}

export function validPreviewQuantity(line, quantity) {
  const rule = line.quantityRule || {min: 1, increment: 1, max: null};
  return Number.isSafeInteger(quantity) && quantity >= rule.min && quantity % rule.increment === 0
    && (rule.max == null || quantity <= rule.max) && Number.isSafeInteger(line.unitPrice * quantity);
}

// Properties are part of line identity: two different messages never share a quantity.
export function previewCartLineKey(line) {
  return JSON.stringify([line.id, Object.entries(line.properties).sort(([a], [b]) => a.localeCompare(b))]);
}

export function addPreviewCartLine(lines, incoming) {
  const key = previewCartLineKey(incoming);
  const existing = lines.find(line => previewCartLineKey(line) === key);
  const quantity = incoming.quantity + (existing?.quantity || 0);
  if (!validPreviewQuantity(incoming, quantity)) return null;
  if (!existing) return [...lines, {...incoming, properties: {...incoming.properties}}];
  return lines.map(line => previewCartLineKey(line) === key ? {...line, quantity} : line);
}
